import { z } from 'zod';
import { colorCodeSchema } from './catalog';
import { uuidSchema } from './common';
import { designAssetViewSchema } from './design-api';
import { resolvedEditAreaDefinitionSchema } from './editor';
import { FONT_FORMATS, fontFamilyNameSchema } from './font';

// ---------------------------------------------------------------------------
// 견적 디자인 보기(뷰어 설계 2026-10-14 V3·V4) — quotes.getDesignView·quoteManagement.getDesignView 응답.
// 견적 사본만으로 그릴 재료를 서명 주소와 함께 준다: 고정 편집영역 정의(고정 색상 하나만), 본문이 가리키는 폰트·잉크
// (견적 뒤 지운 것 포함), 견적에 고정된 이미지. 디자인 본문은 싣지 않는다 — 견적 상세의 design.designBody 를 쓴다
// (주소를 다시 받을 때마다 최대 1MB 본문을 되보내지 않게).
// 폰트·잉크·이미지는 strict 다 — 저장소 키(fileKey) 같은 내부 칸이 섞이면 라우터 출력 검증이 떨어뜨리지 않고 거부한다.
// 편집영역 정의(resolvedEditAreaDefinitionSchema)는 공개 편집기 응답(editor.editAreas.getLatestPublic)과 같은 모양이라,
// 편집영역 에셋 키(photoKey·maskKey·shadingKey·safeAreaKey)와 그 서명 주소를 함께 싣는다.
// ---------------------------------------------------------------------------

/** 사본을 지금 보기에서 열 수 없는 사유 — 사본 형식 버전·고정 편집영역 형식 버전 불일치, 고정 편집영역에 견적 색상이 없음, 디자인 자체가 없는 일반 견적(NO_DESIGN). */
export const QUOTE_DESIGN_VIEW_UNSUPPORTED_REASONS = [
  'DESIGN_FORMAT',
  'EDIT_AREA_FORMAT',
  'COLOR_MISSING',
  'NO_DESIGN',
] as const;
export type QuoteDesignViewUnsupportedReason =
  (typeof QUOTE_DESIGN_VIEW_UNSUPPORTED_REASONS)[number];

/** 폰트 — 브라우저가 FontFace 로 읽을 서명 주소(VIEW_URL_EXPIRES_IN_SECONDS 동안 유효). 삭제된 폰트도 싣는다. */
export const quoteDesignViewFontSchema = z.strictObject({
  id: uuidSchema,
  familyName: fontFamilyNameSchema,
  format: z.enum(FONT_FORMATS),
  fileUrl: z.url(),
});
export type QuoteDesignViewFont = z.infer<typeof quoteDesignViewFontSchema>;

/** 잉크 — 칠할 색만 싣는다. 삭제된 잉크도 싣는다. */
export const quoteDesignViewInkSchema = z.strictObject({
  id: uuidSchema,
  colorCode: colorCodeSchema,
});
export type QuoteDesignViewInk = z.infer<typeof quoteDesignViewInkSchema>;

/** 견적에 고정된 READY 이미지 — 디자인 조회의 이미지 표시 정보와 같은 칸을 strict 로 둔다(원본은 non-strict 라 키를 떨어뜨린다). */
export const quoteDesignViewAssetSchema = z.strictObject(designAssetViewSchema.shape);
export type QuoteDesignViewAsset = z.infer<typeof quoteDesignViewAssetSchema>;

export const quoteDesignViewSchema = z.discriminatedUnion('status', [
  z.strictObject({
    status: z.literal('READY'),
    /** 견적에 고정된 편집영역 버전 행 id — 상품 최신 편집영역이 아니다. */
    editAreaId: uuidSchema,
    /** 견적 색상. definition.colors 에는 이 색상 하나만 있다. */
    colorOptionId: uuidSchema,
    definition: resolvedEditAreaDefinitionSchema,
    fonts: z.array(quoteDesignViewFontSchema),
    inkColors: z.array(quoteDesignViewInkSchema),
    assets: z.array(quoteDesignViewAssetSchema),
  }),
  z.strictObject({
    status: z.literal('UNSUPPORTED'),
    reason: z.enum(QUOTE_DESIGN_VIEW_UNSUPPORTED_REASONS),
  }),
]);
export type QuoteDesignView = z.infer<typeof quoteDesignViewSchema>;
export type QuoteDesignViewReady = Extract<QuoteDesignView, { status: 'READY' }>;
